import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { updateSopTags, serviceLineLabel, type ServiceLineTag } from '@/api/sops.api';

interface Props {
  open: boolean;
  onClose: () => void;
  slug: string;
  initialTags: ServiceLineTag[];
}

const ALL_TAGS: ServiceLineTag[] = ['kwf', 'kwpl'];

/**
 * Edit the service-line tags on a SOP. Visible only to integrator+ roles
 * (the SopDetailPage gates the open button). Mutating compliance metadata
 * requires a documented `reasonForChange` per cGMP §111.180(c) — the form
 * blocks submit until the field is non-empty.
 *
 * On success, invalidates the SOP list + detail queries so the change shows
 * up immediately without a page reload.
 */
export function SopTagsModal({ open, onClose, slug, initialTags }: Props) {
  const [tags, setTags] = useState<Set<ServiceLineTag>>(() => new Set(initialTags));
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => updateSopTags(slug, { serviceLineTags: Array.from(tags), reasonForChange: reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-sops'] });
      void queryClient.invalidateQueries({ queryKey: ['compliance-sop', slug] });
      onClose();
    },
  });

  function toggle(tag: ServiceLineTag): void {
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!reason.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit service-line tags" size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <p className="text-[13px] text-kw-muted">
          Tag this SOP for one or both service lines. The FDA audit covers <strong>KWF</strong> only — tagging
          lets us scope the audit binder to in-scope documents.
        </p>

        <div className="space-y-2">
          {ALL_TAGS.map((tag) => (
            <label
              key={tag}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md border border-kw-border p-3 hover:bg-kw-bg"
            >
              <input
                type="checkbox"
                checked={tags.has(tag)}
                onChange={() => {
                  toggle(tag);
                }}
                className="size-4 accent-kw-cerulean"
              />
              <span className="text-[14px] font-medium text-kw-shark">{serviceLineLabel(tag)}</span>
            </label>
          ))}
        </div>

        <Input
          label="Reason for change"
          name="reasonForChange"
          required
          placeholder="e.g. scoping for FDA audit on 2026-05-12"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          hint="Required by cGMP §111.180(c). Captured in the audit log."
        />

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!reason.trim()}>
            Save tags
          </Button>
        </div>
      </form>
    </Modal>
  );
}
